import React, { useEffect, useRef, useState, useCallback } from "react";

const PRODUCTS = [
  {
    id: 1,
    number: "01",
    name: "OMNIX AI",
    tagline: "Intelligent Automation Platform",
    desc: "A unified AI operating system that orchestrates workflows, connects your tools, and makes decisions at machine speed.",
    logo: "omnix",
    accent: "#8B5CF6",
    rgb: "139,92,246",
    tags: ["Workflow", "Automation", "Decision Engine"],
    stat1: { value: "10×", label: "Faster ops" },
    stat2: { value: "98%", label: "Accuracy" },
  },
  {
    id: 2,
    number: "02",
    name: "CONCORD.AI",
    tagline: "Contract Intelligence Engine",
    desc: "AI-powered contract analysis, risk detection, and negotiation assistant. Turn legal complexity into clear actionable insights.",
    logo: "concord",
    accent: "#22C55E",
    rgb: "34,197,94",
    tags: ["Legal AI", "Risk Analysis", "NLP"],
    stat1: { value: "85%", label: "Time saved" },
    stat2: { value: "3×", label: "Deal velocity" },
  },
  {
    id: 3,
    number: "03",
    name: "INTELIHIRE",
    tagline: "AI-Driven Talent Acquisition",
    desc: "Intelligent hiring that screens, ranks, and engages candidates with precision — reducing time-to-hire while elevating quality.",
    logo: "intelihire",
    accent: "#38BDF8",
    rgb: "56,189,248",
    tags: ["HR Tech", "Screening AI", "Talent"],
    stat1: { value: "60%", label: "Faster hire" },
    stat2: { value: "4×", label: "Quality match" },
  },
];

// Phase state machine per product:
// "stack"    → sitting in the deck (before it's active)
// "rising"   → animating off the stack, standing up
// "expanded" → fullscreen reveal showing product info
// "exiting"  → sliding away as next card comes
// "done"     → completely gone

export default function AIProductsSection({ registerIntent, url = "/" }) {
  /**
   * STEP-BASED STATE MACHINE
   * 
   * Steps for 3 products:
   *  0 = stack(all 3)
   *  1 = expanded(0)       — Product 1 detail
   *  2 = stack(1,2)        — 2 remaining tiles
   *  3 = expanded(1)       — Product 2 detail
   *  4 = stack(2)          — 1 remaining tile
   *  5 = expanded(2)       — Product 3 detail
   *
   * Each scroll forward increments step, backward decrements.
   * Step type alternates: stack → expanded → stack → expanded ...
   */

  /**
   * STEPS (scroll DOWN only shows expanded, scroll UP only shows stack):
   *
   * Step 0: stack(all 3)          ← initial
   * Step 1: expanded(product 0)   ← scroll down from step 0
   * Step 2: stack(2 remaining)    ← scroll down from step 1 (or scroll UP from step 3)
   * Step 3: expanded(product 1)   ← scroll down from step 2
   * Step 4: stack(1 remaining)    ← scroll down from step 3 (or scroll UP from step 5)
   * Step 5: expanded(product 2)   ← scroll down from step 4
   *
   * Scroll UP rule:
   *   - If currently on expanded → go to PREVIOUS stack (never re-show prev expanded)
   *   - If currently on stack    → go to PREVIOUS stack (pop card back on)
   *   - If on step 0 (full stack) → pass to layout (leave section)
   */

  const STEPS = [
    { type: "stack",    from: 0 },   // 0: all 3 tiles
    { type: "expanded", idx:  0 },   // 1: product 1 detail
    { type: "stack",    from: 1 },   // 2: 2 remaining tiles
    { type: "expanded", idx:  1 },   // 3: product 2 detail
    { type: "stack",    from: 2 },   // 4: 1 remaining tile
    { type: "expanded", idx:  2 },   // 5: product 3 detail
  ];

  const [step, setStep]       = useState(0);
  const [animDir, setAnimDir] = useState(1);
  const stepRef               = useRef(0);
  const transitioning         = useRef(false);

  useEffect(() => {
    if (!registerIntent) return;
    registerIntent((dir) => {
      if (transitioning.current) return true; // busy — consume tick

      const cur  = stepRef.current;
      const curS = STEPS[cur];

      if (dir > 0) {
        // ── Scroll DOWN ───────────────────────────────────────────────
        const next = cur + 1;
        if (next >= STEPS.length) return false; // exhausted

        transitioning.current = true;
        stepRef.current = next;
        setAnimDir(1);
        setStep(next);

        // Exit is instant (0.3s) — tile rise takes longer
        const lockMs = STEPS[next].type === "expanded" ? 1400 : 400;
        setTimeout(() => { transitioning.current = false; }, lockMs);
        return true;

      } else {
        // ── Scroll UP ─────────────────────────────────────────────────
        // RULE: never re-show a product detail on scroll up
        // From expanded(N) → go to stack(N-1) i.e. step - 1 (the stack before it)
        // From stack(N)    → go to stack(N-1) i.e. step - 2 (skip the expanded before it)
        // From step 0      → pass to layout

        if (cur === 0) return false; // already at full stack

        let target;
        if (curS.type === "expanded") {
          // Currently showing detail → go back to the stack BEFORE this expanded
          // That's 1 step back (the stack step right before this expanded)
          target = cur - 1;
        } else {
          // Currently on a stack → go to stack 2 steps back
          // (skip the expanded that was between this stack and the previous stack)
          target = Math.max(0, cur - 2);
        }

        transitioning.current = true;
        stepRef.current = target;
        setAnimDir(-1);
        setStep(target);

        const lockMs = 1000;
        setTimeout(() => { transitioning.current = false; }, lockMs);
        return true;
      }
    });
  }, [registerIntent]);

  const currentStep = STEPS[step];

  // Derive everything from currentStep
  const isStackStep    = currentStep.type === "stack";
  const isExpandedStep = currentStep.type === "expanded";
  const expandedIdx    = isExpandedStep ? currentStep.idx : -1;
  const stackFrom      = isStackStep    ? currentStep.from : -1;

  // Background color: use expanded product's color, or first visible stack card
  const bgProd = isExpandedStep
    ? PRODUCTS[expandedIdx]
    : PRODUCTS[stackFrom] || PRODUCTS[0];

  const p = bgProd;

  return (
    <section id="ai-section" className="deck">

      {/* ── Background glow ── */}
      <div
        className="deck__bg-glow"
        style={{
          background: `radial-gradient(circle, rgba(${p.rgb},0.12) 0%, transparent 65%)`,
          transition: "background 0.8s ease",
        }}
      />

      {/* ── Top label ── */}
      {/* <div className="deck__header">
        <span className="deck__label">
          <span className="deck__label-pip" style={{ background: isExpandedStep ? p.accent : "#555" }} />
          AI Products
        </span>
        <span className="deck__counter">
          {isExpandedStep
            ? `${String(expandedIdx + 1).padStart(2,"0")} — ${String(PRODUCTS.length).padStart(2,"0")}`
            : `— — ${String(PRODUCTS.length).padStart(2,"0")}`
          }
        </span>
      </div> */}

      {/* ── THE STACK ── */}
      <div className={`deck__scene ${isStackStep ? "deck__scene--stacked" : "deck__scene--spread"}`}>

        {PRODUCTS.map((prod, i) => {
          // Is this card the currently expanded one?
          const isExpanded = isExpandedStep && i === expandedIdx;

          // Is this card in the visible stack?
          // Stack shows cards from stackFrom onwards
          const inStack = isStackStep && i >= stackFrom;
          const stackPos = inStack ? i - stackFrom : 0;

          // Is this card animating in (step just changed to expanded)?
          const isRising = isExpanded && animDir === 1;

          // Class logic — simple and clean
          let cls = "deck__card";
          if (isExpanded)  cls += " deck__card--expanded";
          if (inStack)     cls += " deck__card--stacked";
          if (!isExpanded && !inStack) cls += " deck__card--done";

          const stackOffset = inStack ? stackPos : 0;

          return (
            <div
              key={prod.id}
              className={cls}
              style={{
                "--accent":  prod.accent,
                "--rgb":     prod.rgb,
                "--stack-i": stackOffset,
                zIndex: isExpanded ? 20 : inStack ? PRODUCTS.length - stackPos : 0,
                // Pass accent to border glow on stacked tiles
                borderTopColor: inStack ? `rgba(${prod.rgb}, 0.65)` : undefined,
              }}
            >
              {/* Card face — shown when stacked */}
              <div className="deck__card-face">
                <div className="deck__card-logo-wrap">
                  <img
                    src={`${url}images/products/${prod.logo}.png`}
                    alt={prod.name}
                    className="deck__card-logo"
                    onError={e => {
                      e.target.style.display = "none";
                      e.target.nextSibling.style.display = "flex";
                    }}
                  />
                  <div className="deck__card-logo-fb" style={{ color: prod.accent }}>
                    {prod.name.slice(0,2)}
                  </div>
                </div>
                {/* <p className="deck__card-name" style={{ color: prod.accent }}>
                  {prod.name}
                </p> */}
              </div>

              {/* Expanded content */}
              {isExpanded && (
                <div className={`deck__expanded deck__expanded--visible`} key={`exp-${step}`}>

                  {/* Giant background number */}
                  <div className="deck__exp-bg-num" style={{ color: `rgba(${prod.rgb},0.06)` }}>
                    {prod.number}
                  </div>

                  {/* Top accent line */}
                  <div className="deck__exp-accent-line" style={{ background: `linear-gradient(90deg, ${prod.accent}, transparent)` }} />

                  <div className="deck__expanded-inner">

                    {/* LEFT col */}
                    <div className="deck__exp-left">

                      {/* Logo */}
                      <div className="deck__exp-logo-box" style={{ borderColor: `rgba(${prod.rgb},0.3)`, background: `rgba(${prod.rgb},0.08)` }}>
                        <img src={`${url}images/products/${prod.logo}.png`} alt={prod.name} className="deck__exp-logo-img"
                          onError={e => { e.target.style.display="none"; e.target.nextSibling.style.display="flex"; }} />
                        <div className="deck__exp-logo-fb" style={{ color: prod.accent }}>{prod.name.slice(0,2)}</div>
                      </div>

                      {/* Stats */}
                      <div className="deck__exp-stats">
                        {[prod.stat1, prod.stat2].map((s, si) => (
                          <div key={s.label} className="deck__exp-stat">
                            <span className="deck__exp-stat-val" style={{ color: prod.accent }}>{s.value}</span>
                            <span className="deck__exp-stat-label">{s.label}</span>
                          </div>
                        ))}
                      </div>

                    </div>

                    {/* RIGHT col */}
                    <div className="deck__exp-right">

                      {/* Tag */}
                      <div className="deck__exp-tag-row">
                        <span className="deck__exp-tag-pip" style={{ background: prod.accent }} />
                        <span className="deck__exp-tagline" style={{ color: prod.accent }}>{prod.tagline}</span>
                        <span className="deck__exp-counter">{prod.number} / 0{PRODUCTS.length}</span>
                      </div>

                      {/* Name */}
                      <h2 className="deck__exp-name">{prod.name}</h2>

                      {/* Divider */}
                      <div className="deck__exp-divider" style={{ background: `linear-gradient(90deg, ${prod.accent}55, transparent)` }} />

                      {/* Desc */}
                      <p className="deck__exp-desc">{prod.desc}</p>

                      {/* Tags */}
                      <div className="deck__exp-tags">
                        {prod.tags.map(t => (
                          <span key={t} className="deck__exp-pill"
                            style={{ borderColor: `rgba(${prod.rgb},0.3)`, color: prod.accent, background: `rgba(${prod.rgb},0.06)` }}>
                            {t}
                          </span>
                        ))}
                      </div>

                      {/* CTA */}
                      <div className="deck__exp-actions">
                        <button className="deck__exp-cta" style={{ "--accent": prod.accent, "--rgb": prod.rgb }}>
                          {/* <span>Explore {prod.name}</span> */}
                          <span>Explore</span>
                          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                            <path d="M2 7h10M8 3l4 4-4 4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </button>
                        <span className="deck__exp-scroll-hint">Scroll to continue</span>
                      </div>

                    </div>
                  </div>
                </div>
              )}

              {/* Colored top edge glow */}
              <div className="deck__card-edge" style={{ background: prod.accent }} />
            </div>
          );
        })}
      </div>

      {/* ── Scroll hint ── */}
      <div className={`deck__hint ${step > 0 ? "deck__hint--hide" : ""}`}>
        <div className="deck__hint-icon">
          <span />
        </div>
        <span className="deck__hint-text">Scroll to explore products</span>
      </div>

      {/* ── Progress ── */}
      {/* <div className="deck__progress">
        {PRODUCTS.map((prod, i) => {
          const isDone   = isExpandedStep && i < expandedIdx;
          const isActive = isExpandedStep && i === expandedIdx;
          return (
            <div
              key={prod.id}
              className={`deck__progress-dot ${isDone ? "deck__progress-dot--done" : ""} ${isActive ? "deck__progress-dot--active" : ""}`}
              style={isActive ? { background: prod.accent, boxShadow: `0 0 8px ${prod.accent}` } : {}}
            />
          );
        })}
      </div> */}

    </section>
  );
}
